/**
 * 1.3.81: YUIChat 项目 - 注册页面
 * 非SSO账号注册流程：
 * 1. 选择用户类型（个人/企业）
 * 2. 个人用户：输入邮箱密码 -> 发送验证邮件
 * 3. 企业用户：跳转到 BusinessSetupPage 填写企业信息和邮箱密码
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Loader2, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { signUpWithEmail } from '../services/authService';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { translateAuthError } from '../utils/errorTranslation';
import { validatePasswordStrength, PASSWORD_MIN_LENGTH } from '../utils/passwordValidation';

type RegisterStep = 'select' | 'personal-form' | 'verify';

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<RegisterStep>('select');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // 1.3.89: 密码显示/隐藏
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // 1.3.89: 确认密码显示/隐藏
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordChecks = useMemo(() => validatePasswordStrength(password).checks, [password]);

  // 选择个人用户
  const handleSelectPersonal = () => {
    setStep('personal-form');
    setError(null);
  };

  // 选择企业用户 - 跳转到企业注册页面
  const handleSelectBusiness = () => {
    navigate('/business-register');
  };

  // 个人用户注册提交
  const handlePersonalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 验证密码强度
    const pwValidation = validatePasswordStrength(password);
    if (!pwValidation.isValid) {
      setError(t('passwordRequirement'));
      return;
    }

    // 验证密码一致性
    if (password !== confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await signUpWithEmail(email, password);
      
      if (result.error) {
        setError(translateAuthError(result.error.message, t));
      } else {
        // 注册成功，显示验证提示
        setStep('verify');
      }
    } catch (err: any) {
      setError(err.message || t('operationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // 返回到登录页面
  const handleBackToLogin = () => {
    navigate('/auth');
  };

  // 返回到选择页面
  const handleBackToSelect = () => {
    setStep('select');
    setError(null);
  };

  // 验证邮件提示页面
  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center animate-in fade-in duration-300">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('verifyYourEmail')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('verificationEmailSent')} <strong>{email}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('checkYourInbox')}
          </p>
          <button
            onClick={handleBackToLogin}
            className="w-full py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300"
          >
            {t('backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // 个人用户注册表单
  if (step === 'personal-form') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        {/* 返回按钮 - 左上角 */}
        <button
          onClick={handleBackToSelect}
          className="absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
          title={t('back')}
        >
          <ArrowLeft className="w-6 h-6" />
        </button>

        {/* 语言切换按钮 - 右上角 */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher direction="down" />
        </div>

        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              {/* 简约扁平风格图标 */}
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{t('register.personalRegisterTitle')}</h1>
              <p className="text-gray-500 mt-2">{t('register.personalRegisterSubtitle')}</p>
            </div>

            <form onSubmit={handlePersonalSubmit} className="space-y-6">
              {/* 邮箱 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('email')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* 密码 - 1.3.89: 添加显示/隐藏按钮 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('password')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {[
                    { key: 'length', label: t('passwordRules.length', '至少8个字符') },
                    { key: 'letter', label: t('passwordRules.letter', '包含字母') },
                    { key: 'digit', label: t('passwordRules.digit', '包含数字') },
                    { key: 'special', label: t('passwordRules.special', '包含特殊符号（如 !@#$%）') },
                  ].map(rule => (
                    <p key={rule.key} className={`text-xs flex items-center gap-1 ${
                      passwordChecks[rule.key as keyof typeof passwordChecks]
                        ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      <span>{passwordChecks[rule.key as keyof typeof passwordChecks] ? '✓' : '○'}</span>
                      {rule.label}
                    </p>
                  ))}
                </div>
              </div>

              {/* 确认密码 - 1.3.89: 添加显示/隐藏按钮 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('confirmPassword')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* 错误提示 */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* 提交按钮 */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 shadow-lg hover:shadow-xl flex items-center justify-center"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    {t('register.registering')}
                  </span>
                ) : t('register.registerAndVerify')}
              </button>
            </form>
          </div>

          {/* 底部提示 */}
          <p className="text-center text-gray-500 text-sm mt-6">
            {t('register.alreadyHaveAccount')}{' '}
            <button
              onClick={handleBackToLogin}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {t('login')}
            </button>
          </p>
        </div>
      </div>
    );
  }

  // 选择用户类型页面（默认）
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* 返回按钮 - 左上角 */}
      <button
        onClick={handleBackToLogin}
        className="absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
        title={t('onboarding.backToLogin')}
      >
        <ArrowLeft className="w-6 h-6" />
      </button>

      {/* 语言切换按钮 - 右上角 */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher direction="down" />
      </div>
      
      <div className="w-full max-w-3xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            {t('register.welcomeTitle')}
          </h1>
          <p className="mt-4 text-gray-600 text-lg">
            {t('register.selectUserType')}
          </p>
        </div>

        {/* 选择卡片 */}
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          {/* Individual 个人用户卡片 */}
          <div 
            className="bg-white rounded-2xl shadow-lg p-6 border-2 border-transparent hover:border-indigo-500 hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col"
            onClick={handleSelectPersonal}
          >
            {/* 类型标签 */}
            <div className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full mb-4 self-start">
              Individual
            </div>
            
            <div className="flex items-center gap-3 mb-2">
              {/* 简约扁平风格图标 */}
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('onboarding.personalUser')}</h2>
                <p className="text-gray-500 text-xs">{t('onboarding.individualUsers')}</p>
              </div>
            </div>
            
            {/* 价格 */}
            <div className="py-4 border-b border-gray-100 mb-4">
              <div className="flex items-baseline gap-3">
                <span className="text-xl text-gray-400 line-through">¥2,500</span>
                <span className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">¥0</span>
                <span className="text-gray-500 text-sm">/{t('onboarding.perPersonMonth')}</span>
              </div>
              <div className="mt-2">
                <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  {t('onboarding.free7Days')}
                </span>
              </div>
            </div>
            
            {/* 功能列表 - flex-grow 让按钮对齐底部 */}
            <ul className="space-y-2.5 mb-4 text-sm flex-grow">
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedChat')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.publicLink5000')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedKB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.storage1GBPerson')}
              </li>
              <li className="flex items-center text-gray-400">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('onboarding.multiUser')}
              </li>
              <li className="flex items-center text-gray-400">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('onboarding.sharedKB')}
              </li>
            </ul>

            {/* 底部区域 - mt-auto 确保对齐底部 */}
            <div className="mt-auto">
              {/* 超额提示 */}
              <p className="text-xs text-gray-400 mb-4">{t('onboarding.overageNote')}</p>

              <button 
                className="w-full py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 hover:scale-[1.02] transition-all duration-300 group-hover:shadow-lg"
              >
                {t('onboarding.select')}
              </button>
            </div>
          </div>

          {/* Standard 企业用户卡片 */}
          <div 
            className="bg-white rounded-2xl shadow-lg p-6 border-2 border-transparent hover:border-purple-500 hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col"
            onClick={handleSelectBusiness}
          >
            {/* 类型标签 */}
            <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full mb-4 self-start">
              Standard
            </div>
            
            <div className="flex items-center gap-3 mb-2">
              {/* 简约扁平风格图标 */}
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('onboarding.businessUser')}</h2>
                <p className="text-gray-500 text-xs">{t('onboarding.standardUsers')}</p>
              </div>
            </div>
            
            {/* 价格 */}
            <div className="py-4 border-b border-gray-100 mb-4">
              <div className="flex items-baseline gap-3">
                <span className="text-xl text-gray-400 line-through">¥2,300</span>
                <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">¥0</span>
                <span className="text-gray-500 text-sm">/{t('onboarding.perPersonMonth')}</span>
              </div>
              <div className="mt-2">
                <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  {t('onboarding.free7Days')}
                </span>
              </div>
            </div>
            
            {/* 功能列表 - flex-grow 让按钮对齐底部 */}
            <ul className="space-y-2.5 mb-4 text-sm flex-grow">
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedChat')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.publicLink10000')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedKB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.storage10GB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.multiUser')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.sharedKB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.rolePermissions')}
              </li>
            </ul>

            {/* 底部区域 - mt-auto 确保对齐底部 */}
            <div className="mt-auto">
              {/* 超额提示 */}
              <p className="text-xs text-gray-400 mb-4">{t('onboarding.overageNote')}</p>

              <button 
                className="w-full py-3 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 hover:scale-[1.02] transition-all duration-300 group-hover:shadow-lg"
              >
                {t('onboarding.select')}
              </button>
            </div>
          </div>
        </div>

        {/* 底部提示 */}
        <p className="text-center text-gray-500 text-sm mt-8">
          {t('register.alreadyHaveAccount')}{' '}
          <button
            onClick={handleBackToLogin}
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            {t('login')}
          </button>
        </p>
      </div>
    </div>
  );
}
